import {
  faExclamationCircle,
  faWrench,
} from "@fortawesome/pro-solid-svg-icons";
import { Stack } from "@mui/material";

import { Alert, Divider, Icon, Link, Typography } from "@/components/ui-v2";
import { useBasicData, useIncidents, useMaintenances } from "@/hooks";
import { useDismissedMaintenances } from "@/hooks/useDismissedMaintenances";
import { describeMaintenanceWindow } from "@/utils/maintenanceWindow";

const DEFAULT_INCIDENT_TITLE =
  "Our team is investigating an issue that may affect your experience.";
const DEFAULT_MAINTENANCE_TITLE = "Layer has scheduled maintenance.";

export const AppWarnings = () => {
  const { workspace, error } = useBasicData();
  const { incidents } = useIncidents();
  const { maintenances } = useMaintenances();
  const { isDismissed, dismiss } = useDismissedMaintenances();

  if (error || !workspace) {
    return null;
  }

  // An incident outranks a maintenance, which is planned and so announced rather than warned about.
  // Only a maintenance can be closed; one the reader closed gives way to the next.
  const currentIncident = incidents?.[0];
  const currentMaintenance = maintenances?.find(
    (maintenance) => !isDismissed(maintenance),
  );
  const currentNotice = currentIncident ?? currentMaintenance;
  if (!currentNotice) {
    return null;
  }
  const schedule =
    !currentIncident && currentMaintenance
      ? describeMaintenanceWindow(currentMaintenance)
      : null;

  return (
    <Alert
      severity={currentIncident ? "warning" : "info"}
      onClose={
        !currentIncident && currentMaintenance
          ? () => dismiss(currentMaintenance)
          : undefined
      }
      closeText="Dismiss maintenance notice"
      icon={
        <Icon
          icon={currentIncident ? faExclamationCircle : faWrench}
          size="small"
        />
      }
    >
      <Stack spacing={1} divider={<Divider />}>
        <Typography variant="caption" fontWeight={700}>
          {currentNotice.name ||
            (currentIncident
              ? DEFAULT_INCIDENT_TITLE
              : DEFAULT_MAINTENANCE_TITLE)}
        </Typography>
        {schedule && <Typography variant="caption">{schedule}</Typography>}
        <Typography variant="caption">
          <span>
            You can follow the status of this{" "}
            {currentIncident ? "incident" : "maintenance"}{" "}
          </span>
          <Link
            href={currentNotice.url}
            sx={{ fontWeight: "bold", textDecoration: "underline" }}
          >
            here
          </Link>
          <span>.</span>
        </Typography>
      </Stack>
    </Alert>
  );
};
